import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import './styles/tailwind.css'

import { QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import i18next from './i18n/config.ts'

import { ToastProvider } from '@components/toast/Toast.tsx'
import { AuthenticationProvider } from '@features/auth/context/AuthenticationContext.tsx'
import { ErrorBoundary, Provider } from '@rollbar/react'
import { queryClient } from '@services/utils'
import { I18nextProvider } from 'react-i18next'

import { ChatWidgetContextProvider } from '@features/chat/context/ChatWidgetContext.tsx'
import { PostHogProvider } from 'posthog-js/react'
import posthog from 'posthog-js'
import { analyticsEnabled, initAnalytics } from '@/lib/analytics'
import rollbar from './rollbarConfig'
import CookieConsentBanner from './components/CookieConsent'

const rollbarToken = import.meta.env.VITE_ROLLBAR_ACCESS_TOKEN

// Opted out until the visitor accepts analytics cookies (GDPR)
initAnalytics('platform')

const AppContent = () => (
  <BrowserRouter>
    <I18nextProvider i18n={i18next} defaultNS={'sk'}>
      <ToastProvider>
        <QueryClientProvider client={queryClient}>
          <AuthenticationProvider>
            <ChatWidgetContextProvider>
              <App />
              <CookieConsentBanner />
            </ChatWidgetContextProvider>
          </AuthenticationProvider>
        </QueryClientProvider>
      </ToastProvider>
    </I18nextProvider>
  </BrowserRouter>
)

const AppWithPostHog = () =>
  analyticsEnabled ? (
    <PostHogProvider client={posthog}>
      <AppContent />
    </PostHogProvider>
  ) : (
    <AppContent />
  )

createRoot(document.getElementById('root')!).render(
  rollbarToken ? (
    <Provider instance={rollbar}>
      <ErrorBoundary>
        <AppWithPostHog />
      </ErrorBoundary>
    </Provider>
  ) : (
    <AppWithPostHog />
  )
)
